/**
 * atmArchive — pure helpers for the ATM template archive system (v7.6.0).
 *
 * An ATM template is "archived" when it's been removed from NinjaTrader's
 * template folder and missing from the journal's syncs for more than the
 * grace period (currently 1 hour — see useAtmManagement.ARCHIVE_GRACE_MS).
 * Archived templates retain all historical analytics + trades + version
 * snapshots, but Coach treats them as evidence rather than as actively-
 * recommended ATMs.
 *
 * The archive state lives on the LATEST version snapshot for each template
 * in `ntTemplateVersions[atmName][0]`:
 *   - lastSeenAt : ISO  — refreshed every sync the template is found
 *   - archivedAt : ISO|null  — set when missing past the grace period;
 *                              cleared the moment the template reappears
 *
 * All helpers in this module are pure functions over the
 * `ntTemplateVersions` map. No side effects, no hooks, no IPC. Easy to
 * unit-test and safe to call from any context (renderer components,
 * analyzers, electron main process if ever needed).
 */

/**
 * Is this ATM currently archived?
 *
 * @param {Object} ntTemplateVersions  The full versions map from useAtmManagement
 * @param {string} atmName             Template name (e.g., '20pSL - 3C - 2R')
 * @returns {boolean}
 */
export const isAtmArchived = (ntTemplateVersions, atmName) => {
  if (!ntTemplateVersions || !atmName || typeof atmName !== 'string') return false;
  const versions = ntTemplateVersions[atmName];
  if (!Array.isArray(versions) || versions.length === 0) return false;
  return Boolean(versions[0]?.archivedAt);
};

/**
 * When was this ATM archived? Returns the ISO string, or null if active /
 * unknown / never archived.
 */
export const getArchivedAt = (ntTemplateVersions, atmName) => {
  if (!ntTemplateVersions || !atmName || typeof atmName !== 'string') return null;
  const versions = ntTemplateVersions[atmName];
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions[0]?.archivedAt || null;
};

/**
 * When was this ATM last seen in a NinjaTrader sync? Returns the ISO
 * string, or null if never seen by the new sync code (legacy data).
 */
export const getLastSeenAt = (ntTemplateVersions, atmName) => {
  if (!ntTemplateVersions || !atmName || typeof atmName !== 'string') return null;
  const versions = ntTemplateVersions[atmName];
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions[0]?.lastSeenAt || null;
};

/**
 * Partition all known ATM names into { active, archived } arrays. Names
 * are returned sorted alphabetically within each bucket for stable UI.
 */
export const partitionAtms = (ntTemplateVersions) => {
  const active = [];
  const archived = [];
  if (!ntTemplateVersions || typeof ntTemplateVersions !== 'object') {
    return { active, archived };
  }
  for (const [name, versions] of Object.entries(ntTemplateVersions)) {
    if (!Array.isArray(versions) || versions.length === 0) continue;
    if (versions[0]?.archivedAt) {
      archived.push(name);
    } else {
      active.push(name);
    }
  }
  active.sort((a, b) => a.localeCompare(b));
  archived.sort((a, b) => a.localeCompare(b));
  return { active, archived };
};

/**
 * Format an ATM display label, appending " (archived)" when applicable.
 * Used by the Edit Trade Modal dropdown + anywhere else the user picks
 * an ATM by name and should know retired ones aren't really pickable.
 */
export const formatAtmDisplayLabel = (ntTemplateVersions, atmName) => {
  if (!atmName) return '';
  return isAtmArchived(ntTemplateVersions, atmName)
    ? `${atmName} (archived)`
    : atmName;
};

/**
 * Compact relative-time label like "12d ago" / "3h ago" / "just now"
 * for the archive badge. Returns '—' for invalid timestamps.
 */
export const formatArchiveAge = (archivedAtIso, nowMs = Date.now()) => {
  if (!archivedAtIso) return '—';
  const archivedMs = new Date(archivedAtIso).getTime();
  if (!Number.isFinite(archivedMs)) return '—';
  const ageSec = Math.max(0, Math.floor((nowMs - archivedMs) / 1000));
  if (ageSec < 60) return 'just now';
  const m = Math.floor(ageSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
};
